'use client';

/**
 * category-tree-shell — the shared visual + interaction primitive for a
 * two-level CATEGORY-BROWSE tree in the explorer rail. ONE language for both
 * trees that live in the mode11 rail, so they can no longer drift:
 *   - CategoryTreeCard  — catalogue browse (category → product_type facets)
 *   - CustomProductsExplorerCard — the buyer's BW_ products (category → products)
 *
 * They differ ONLY in data source + what a category / child click does; the
 * ROW look (bordered bg-card row, chevron gutter, depthRamp typography, capitalised
 * label, hover-revealed count, active state, indented child tree) is factored here
 * (klyde-cards: "cards are meant to be borrowed; factor the shared shell into a
 * primitive"). Purely presentational — no data fetch, no store, no capture export;
 * each owning card wires selection + fetch + click behaviour.
 *
 * Visual idiom is mode4's vendor_list tree (the settled explorer layout), lifted
 * verbatim from CategoryTreeCard so the catalogue browse renders byte-identically.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useState, type ReactNode } from 'react';
import { ChevronRight, Loader2, Plus, type LucideIcon } from 'lucide-react';
import { text, treeDepth as depthRamp, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

/** Header (icon + title) + optional "+" affordance, wrapping the row list.
 *
 * `collapsible` turns the header into a toggle (chevron + click) that hides the
 * whole row list — for space-heavy trees whose buckets shouldn't claim the rail
 * on first paint (Custom Products). `defaultCollapsed` sets the initial state
 * (ephemeral view state, per-session). `count` rides the header so a collapsed
 * section still shows it holds N items (mirrors the My Favourites row). The
 * catalogue browse tree opts out (stays always-open — the settled layout). */
export function CategoryTreeFrame({
  title,
  icon: Icon,
  iconClassName,
  onAdd,
  addLabel,
  collapsible = false,
  defaultCollapsed = false,
  count,
  onSelect,
  selected = false,
  children,
}: {
  title: string;
  icon: LucideIcon;
  iconClassName?: string;
  onAdd?: () => void;
  addLabel?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  count?: number | null;
  // Fired when a collapsible header is EXPANDED — the "select this group" hook.
  // Custom Products uses it to paint all its category tiles on the canvas (the
  // collections landing). Collapse stays rail-only, so a drilled canvas is kept.
  onSelect?: () => void;
  selected?: boolean; // header active state — the canvas is showing this landing
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(collapsible ? defaultCollapsed : false);
  const open = !collapsible || !collapsed;

  // Expanding selects the group (paint its landing on the canvas); collapsing is
  // rail-only and leaves the canvas untouched (a drilled-into category survives).
  const toggle = () => {
    const nextCollapsed = !collapsed;
    setCollapsed(nextCollapsed);
    if (!nextCollapsed) onSelect?.();
  };

  // One row style everywhere — the doctrine is "everything is a card",
  // not "cards for interactive rows, plain labels for static ones".
  // Interactivity (click, hover, chevron) is a SEPARATE concern from the
  // base card shell: collapsible (Custom Products) gets both; static
  // (CategoryTreeCard's plain "Categories" title, no click target) gets
  // the same bordered-card shape minus the button/chevron/hover affordances
  // a non-interactive row shouldn't carry.
  if (!collapsible) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className="space-y-1">
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between gap-2 px-2 py-1.5">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="w-4 shrink-0" />
              <Icon className={cn('h-3.5 w-3.5 shrink-0', iconClassName)} />
              <span className={cn(depthRamp[0], 'truncate flex-1')}>{title}</span>
              {count != null && count > 0 && <span className={cn(text.muted, 'shrink-0')}>{count}</span>}
            </div>
            {onAdd && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAdd(); }}
                aria-label={addLabel}
                title={addLabel}
                className="p-0.5 rounded shrink-0 text-primary hover:bg-accent/50 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <ul className="space-y-1">{children}</ul>
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="space-y-1">
      <div
        className={cn(
          'rounded-lg border transition-colors',
          selected ? 'border-primary/30 bg-primary/5' : 'border-border bg-card hover:bg-accent/30',
        )}
      >
        <div className="flex items-center justify-between gap-2 px-2 py-1.5">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
          >
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                open && 'rotate-90',
              )}
            />
            <Icon className={cn('h-3.5 w-3.5 shrink-0', iconClassName)} />
            <span className={cn(depthRamp[0], 'truncate flex-1', selected && 'text-primary font-medium')}>
              {title}
            </span>
            {count != null && count > 0 && <span className={cn(text.muted, 'shrink-0')}>{count}</span>}
          </button>
          {onAdd && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              aria-label={addLabel}
              title={addLabel}
              className="p-0.5 rounded shrink-0 text-primary hover:bg-accent/50 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {open && <ul className="space-y-1">{children}</ul>}
    </div>
  );
}

/** The "All" / clear-scope row — no chevron, no count. */
export function CategoryAllRow({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <div
        onClick={onClick}
        className={cn(
          'relative rounded-lg border bg-card transition-colors cursor-pointer',
          active ? 'border-primary/30 bg-primary/5' : 'border-border hover:bg-accent/30',
        )}
      >
        <div className="flex items-center gap-2 px-2 py-1.5">
          <span className="w-4 shrink-0" />
          <span className={cn(depthRamp[0], 'truncate flex-1', active && 'text-primary font-medium')}>
            {label}
          </span>
        </div>
      </div>
    </li>
  );
}

/** A level-1 category row (SELECTABLE — click drives the canvas) + its expanded
 *  child slot. `children` is the level-2 content the owning card renders. */
export function CategoryBucketRow({
  label,
  count,
  countSuffix = 'p',
  active,
  expandable,
  expanded,
  onToggleExpand,
  onPick,
  children,
}: {
  label: string;
  count?: number | null;
  countSuffix?: string;
  active: boolean;
  expandable: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onPick: () => void;
  children?: ReactNode;
}) {
  return (
    <li>
      <div
        className={cn(
          'relative rounded-lg border bg-card transition-colors',
          active ? 'border-primary/30 bg-primary/5' : 'border-border hover:bg-accent/30',
        )}
      >
        <div className="group flex items-center gap-2 px-2 py-1.5 cursor-pointer" onClick={onPick}>
          {expandable ? (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
              className="p-0.5 hover:bg-muted rounded shrink-0"
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              <ChevronRight
                className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')}
              />
            </button>
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <span className={cn(depthRamp[0], 'capitalize truncate flex-1', active && 'text-primary font-medium')}>
            {label}
          </span>
          {count != null && count > 0 && (
            <span className={cn(text.muted, 'shrink-0 opacity-0 group-hover:opacity-100 transition-opacity')}>
              {count}{countSuffix}
            </span>
          )}
        </div>

        {expanded && children != null && (
          <div className="ml-3 mb-1 pl-1 border-l border-border/50">{children}</div>
        )}
      </div>
    </li>
  );
}

/** A level-2 child row (a product_type in browse, a product in custom products).
 *  `capitalize` on for type facets, off for product names; `trailing` carries the
 *  count / price / kebab. */
export function CategoryChildRow({
  label,
  active,
  onClick,
  trailing,
  capitalize = true,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  trailing?: ReactNode;
  capitalize?: boolean;
  title?: string;
}) {
  return (
    <div className="group/leaf flex items-center gap-2 px-2 py-1 rounded hover:bg-accent/30">
      <button
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        title={title ?? label}
        className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer"
      >
        <span className={cn(depthRamp[1], 'truncate flex-1', capitalize && 'capitalize', active && 'text-primary font-medium')}>
          {label}
        </span>
      </button>
      {trailing}
    </div>
  );
}

/** Shared spinner for a lazily-fetched child level. */
export function CategoryChildLoading() {
  return (
    <div className={cn(text.muted, 'flex items-center gap-2 px-2 py-1')}>
      <Loader2 className="h-3 w-3 animate-spin" />
    </div>
  );
}
